import { View } from '../components/orderhistory/types';
import { addPeriod, startOfPeriod } from './dateUtils';
import i18n from '../i18n';

export interface KpiChangeData {
  changeValue: number;
  changeLabel: string;
}

/**
 * Calculate the percentage change between current and previous period values
 * Returns 0 if no comparison can be made
 */
export function calculatePercentageChange(currentValue: number, previousValue: number): number {
  if (previousValue === 0) return 0;
  return ((currentValue - previousValue) / previousValue) * 100;
}

/**
 * Compute the previous-period date range for a given current period and view.
 * Returns null for the 'all' view (no comparison) or any unsupported view.
 *  - day:   same weekday last week (7 days ago)
 *  - week:  the entire previous week
 *  - month: the entire previous month
 */
export function getPreviousPeriodRange(
  view: View,
  startDate: Date,
  endDate: Date
): { previousStartDate: Date; previousEndDate: Date } | null {
  if (view === 'day') {
    const previousStartDate = new Date(startDate);
    previousStartDate.setDate(previousStartDate.getDate() - 7);
    const previousEndDate = new Date(previousStartDate);
    return { previousStartDate, previousEndDate };
  } else if (view === 'week') {
    return {
      previousStartDate: addPeriod(startDate, 'week', -1),
      previousEndDate: addPeriod(endDate, 'week', -1),
    };
  } else if (view === 'month') {
    const previousStartDate = addPeriod(startOfPeriod(startDate, 'month'), 'month', -1);
    // Calculate end of previous month
    const previousEndDate = new Date(previousStartDate);
    previousEndDate.setMonth(previousEndDate.getMonth() + 1);
    previousEndDate.setDate(0); // Last day of the month
    return { previousStartDate, previousEndDate };
  }
  return null;
}

/**
 * Get the appropriate label for the change based on the view type and current date
 */
function getChangeLabel(view: View, currentStartDate: Date): string {
  if (view === 'day') {
    const locale = i18n.language === 'nl' ? 'nl-NL' : 'en-GB';
    const dayName = new Date(currentStartDate.getTime() - 7 * 24 * 60 * 60 * 1000)
      .toLocaleDateString(locale, { weekday: 'short' });
    return i18n.t('common.vsLastDay', { day: dayName });
  } else if (view === 'week') {
    return i18n.t('common.vsLastWeek');
  } else if (view === 'month') {
    return i18n.t('common.vsLastMonth');
  }
  return '';
}

/**
 * Calculate KPI change value and label based on current view
 *
 * @param currentValue - The current period's value
 * @param view - The current time view ('day', 'week', 'month', 'all')
 * @param startDate - Start date of the current period
 * @param endDate - End date of the current period
 * @param calculateValueFn - Function to calculate the value for a given date range
 * @returns KpiChangeData with changeValue and changeLabel, or null if view is 'all' or no data
 */
export function calculateKpiChange<T>(
  currentValue: number,
  view: View,
  startDate: Date,
  endDate: Date,
  calculateValueFn: (data: T[], start: Date, end: Date) => number,
  dataList: T[]
): KpiChangeData | null {
  // No change shown for 'all time' view (or any unsupported view)
  const previousRange = getPreviousPeriodRange(view, startDate, endDate);
  if (!previousRange) {
    return null;
  }

  const { previousStartDate, previousEndDate } = previousRange;

  // Calculate previous period value
  const previousValue = calculateValueFn(dataList, previousStartDate, previousEndDate);

  // If there's no data for previous period, return 0
  if (previousValue === 0) {
    return { changeValue: 0, changeLabel: getChangeLabel(view, startDate) };
  }

  // Calculate percentage change
  const changeValue = calculatePercentageChange(currentValue, previousValue);
  const changeLabel = getChangeLabel(view, startDate);

  return { changeValue, changeLabel };
}
